import React, {useEffect, useMemo, useState} from 'react'
import CtaButton from 'Src/components/Buttons/CtaButton'
import {useFrameContext} from 'Src/contexts/FrameContext'
import {useNavDispatchContext} from 'Src/contexts/NavContext'
import {useAnalytics} from 'Src/services/analytics/useAnalytics'
import {resolveErrorPageMessage} from 'Src/utilities/errorPageMessage'
import {FindQuery} from 'Src/utilities/Helpers'
import useConfig from 'Src/utilities/useConfig'
import {getResolvedSellingPrice, mergeVehicleRecords} from 'Src/utilities/vehicleSellingPrice'

const ErrorContainer = () => {
  const navDispatch = useNavDispatchContext()
  const config = useConfig()
  const {dealership, vehicleData} = useFrameContext()
  const {trackEvent, events} = useAnalytics()
  // Try multiple sources for the phone number
  const dealershipPhone =
    config?.dealershipPhone || dealership?.attributes?.phone || config?.dealership?.phone

  // Get the original message from URL params (legacy immediate-error flows only)
  const originalMessage = FindQuery('message')
  /** Deferred error-screen flow: use existing page copy by cause, not raw API text. */
  const deferredErrorCause = FindQuery('cause')

  const mergedVehicle = useMemo(
    () => mergeVehicleRecords(vehicleData, config?.vehicle) as Record<string, unknown>,
    [config?.vehicle, vehicleData]
  )

  const message = resolveErrorPageMessage({
    dealershipPhone,
    vehicle: vehicleData,
    embedVehicle: config?.vehicle,
    deferredErrorCause,
    originalMessage,
  })

  const handleCloseButtonClick = () => navDispatch({type: 'closeSlideOut'})

  /**
   * Fire Google Analytics event when the DR Error page is shown.
   * The event name depends on the type of error. We also include optional
   * metadata about the dealership and vehicle when available.
   */
  // Track whether we've already sent the error event. This prevents
  // duplicate pushes if the config arrives *after* the component mounts
  // and triggers a re-render.
  const [hasFired, setHasFired] = useState(false)

  useEffect(() => {
    if (hasFired || typeof window === 'undefined') {
      return
    }

    // Determine which error event to send
    let eventName = 'dr_error_view'

    if (message?.includes('Vehicle not in inventory')) {
      eventName = 'dr_error_inventory_not_found'
    } else if (
      deferredErrorCause === 'no_selling_price' ||
      message?.includes('A price is unavailable for this vehicle.')
    ) {
      eventName = 'dr_error_price_unavailable'
    }

    const params = {
      dealer_name: dealership?.attributes?.name || config.dealership?.name,
      vehicle_vin: mergedVehicle.vin,
      vehicle_stock_number: mergedVehicle.stock || mergedVehicle.stock_number,
      vehicle_msrp: mergedVehicle.msrp,
      vehicle_price: getResolvedSellingPrice(mergedVehicle),
    }

    // Re-use central analytics helper so the event passes through the normal GA/GTM pipeline.
    trackEvent({name: eventName}, events.ERROR.actions.ERROR, undefined, undefined, params)
    setHasFired(true)
  }, [config, dealership, message, mergedVehicle, trackEvent, events])

  return (
    <div className="error-bkgd tw-h-screen tw-w-full">
      <div className="error-page tw-px-10">
        <div className="d-none tw-hidden tw-justify-center">
          <h2 className="whoops">Whoops</h2>
        </div>
        <div className="tw-mb-6 tw-flex tw-justify-center">
          <div data-test="messageDiv" id="error-msg" className="error-msg error-msg--contact">
            {message}
          </div>
        </div>
        <div className="tw-mb-5 tw-flex tw-justify-center md:tw-mb-0">
          <CtaButton
            className="tw-w-full md:tw-max-w-md"
            onClick={handleCloseButtonClick}
            data-test="button"
          >
            Close
          </CtaButton>
        </div>
      </div>
    </div>
  )
}

export default ErrorContainer
